import { API_ROUTES, RunPreviewFrameSchema, routePath, } from "@mfa/v5-contracts";
import { ResumeCursor } from "./cursor.js";
import { ApiClientError, isAbortError, isApiClientError, NETWORK_ERROR, readError, } from "./errors.js";
import { SseParser } from "./sse.js";
export const DEFAULT_STREAM_RETRY = {
    baseMs: 500,
    maxMs: 15_000,
    retryAfterMaxMs: 300_000,
    maxAttempts: 10,
    healthyMs: 30_000,
};
/** Statuses worth another try: the server or a proxy in front of it, not the request. */
export const isRetryableStatus = (status) => status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
/** `Retry-After` in milliseconds (seconds or an HTTP date), or undefined. */
function retryAfterMs(headers) {
    const value = headers.get("retry-after");
    if (!value)
        return undefined;
    const seconds = Number(value);
    if (Number.isFinite(seconds))
        return Math.max(0, seconds * 1000);
    const at = Date.parse(value);
    return Number.isNaN(at) ? undefined : Math.max(0, at - Date.now());
}
/** Waits `ms`, or less when `signal` aborts first; never rejects. */
export function sleep(ms, signal) {
    return new Promise((resolve) => {
        if (signal?.aborted)
            return resolve();
        const done = () => {
            clearTimeout(timer);
            signal?.removeEventListener("abort", done);
            resolve();
        };
        const timer = setTimeout(done, ms);
        signal?.addEventListener("abort", done, { once: true });
    });
}
const dropped = (why, cause) => new ApiClientError({ code: NETWORK_ERROR, status: 0, message: why }, { cause });
function parseFrame(data, what) {
    try {
        return JSON.parse(data);
    }
    catch (error) {
        throw new ApiClientError({
            code: "INVALID_RESPONSE",
            status: 200,
            message: `the stream sent a ${what} that is not JSON`,
        }, { cause: error });
    }
}
/** A view frame's data, or null when it is not one this client can read (it is then skipped). */
function viewFrame(data) {
    try {
        const value = JSON.parse(data);
        return value && typeof value.throughSequence === "number" && "view" in value
            ? { throughSequence: value.throughSequence, view: value.view }
            : null;
    }
    catch {
        return null;
    }
}
/** A preview frame's data, or null when RunPreviewFrameSchema does not read it (it is then skipped). */
function previewFrame(data) {
    try {
        const parsed = RunPreviewFrameSchema.safeParse(JSON.parse(data));
        return parsed.success ? parsed.data : null;
    }
    catch {
        return null;
    }
}
/** The connection's URL and headers for where the cursor resumes. */
function request(transport, runId, point, untilSettled, view) {
    const query = {
        after: point && "after" in point ? String(point.after) : undefined,
        from: point && "from" in point ? point.from : undefined,
        until: untilSettled ? "settled" : undefined,
        view: view ? "1" : undefined,
    };
    const headers = { accept: "text/event-stream" };
    if (point && "lastEventId" in point)
        headers["last-event-id"] = String(point.lastEventId);
    return {
        url: transport.apiUrl(routePath(API_ROUTES.runEvents, { runId }, query)),
        headers,
    };
}
export async function* streamRunEvents(transport, runId, options = {}) {
    const retry = { ...DEFAULT_STREAM_RETRY, ...options.retry };
    const cursor = new ResumeCursor({ after: options.after, fromHead: options.fromHead });
    const outer = options.signal;
    let failures = 0;
    while (true) {
        if (outer?.aborted)
            return;
        const controller = new AbortController();
        const abort = () => controller.abort();
        outer?.addEventListener("abort", abort, { once: true });
        const before = cursor.last;
        // Set once the stream opens (an ok answer with a body): a refusal or a failed send is never
        // healthy, however long it took to come (a gateway's 504 after its own timeout).
        let openedAt = null;
        let failure;
        let wait;
        try {
            const { url, headers } = request(transport, runId, cursor.resume(), !!options.untilSettled, !!options.view);
            const response = await transport.send(url, { headers, signal: controller.signal });
            if (!response.ok) {
                const refusal = await readError(response);
                if (!isRetryableStatus(response.status))
                    throw refusal;
                wait = retryAfterMs(response.headers);
                failure = refusal;
            }
            else if (!response.body) {
                failure = dropped("the stream answered with no body");
            }
            else {
                openedAt = Date.now();
                const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
                const parser = new SseParser();
                failure = dropped("the stream closed with no end frame");
                try {
                    reading: while (true) {
                        let chunk;
                        try {
                            chunk = await reader.read();
                        }
                        catch (error) {
                            if (controller.signal.aborted)
                                throw error;
                            // A socket the server or a proxy cut mid-body: a drop, not a refusal.
                            const why = error instanceof Error ? (error.cause ?? error) : error;
                            failure = dropped(`the stream broke (${why instanceof Error ? why.message : String(why)})`, error);
                            break;
                        }
                        if (chunk.done)
                            break;
                        for (const item of parser.push(chunk.value)) {
                            if (item.kind === "comment")
                                continue; // `: ping`
                            const { frame } = item;
                            if (frame.event === "state") {
                                const state = parseFrame(frame.data, "state frame");
                                cursor.opened(state);
                                yield { kind: "state", state };
                            }
                            else if (frame.event === "view") {
                                const view = viewFrame(frame.data);
                                if (view)
                                    yield { kind: "view", view };
                            }
                            else if (frame.event === "preview") {
                                const preview = previewFrame(frame.data);
                                if (preview)
                                    yield { kind: "preview", preview };
                            }
                            else if (frame.event === "end") {
                                yield { kind: "end", end: parseFrame(frame.data, "end frame") };
                                return;
                            }
                            else if ((frame.event === null || frame.event === "message") && frame.data) {
                                const envelope = parseFrame(frame.data, "envelope");
                                const sequence = typeof envelope.sequence === "number" ? envelope.sequence : Number(frame.id);
                                if (!Number.isFinite(sequence) || !cursor.accept(sequence))
                                    continue;
                                yield { kind: "envelope", sequence, envelope };
                            }
                            // Any other named frame is one this client does not know: ignored, as the wire says.
                            if (outer?.aborted)
                                break reading;
                        }
                    }
                }
                finally {
                    reader.cancel().catch(() => { });
                }
            }
        }
        catch (error) {
            if (outer?.aborted && (isAbortError(error) || controller.signal.aborted))
                return;
            // Only a drop is retried: a redirect refused at status 0 (a browser's) ends it, as a refusal.
            if (isApiClientError(error) && error.code === NETWORK_ERROR)
                failure = error;
            else
                throw error;
        }
        finally {
            outer?.removeEventListener("abort", abort);
            controller.abort();
        }
        if (outer?.aborted)
            return;
        const healthy = cursor.last !== before || (openedAt !== null && Date.now() - openedAt >= retry.healthyMs);
        failures = healthy ? 1 : failures + 1;
        if (failures > retry.maxAttempts)
            throw failure;
        // The backoff is capped; the server's Retry-After is its minimum, honoured above that cap
        // (a spent read window turns up to 60 s later), so the retry never lands inside the window.
        const backoff = Math.min(retry.baseMs * 2 ** (failures - 1), retry.maxMs);
        const delayMs = Math.max(backoff, Math.min(wait ?? 0, retry.retryAfterMaxMs));
        options.onReconnect?.({ attempt: failures, delayMs, error: failure, resume: cursor.resume() });
        await sleep(delayMs, outer);
    }
}
//# sourceMappingURL=stream.js.map